import React, { useContext } from 'react'

import { Widget } from '@/common/model/recommend/widget'

import Button from '@/components/view/atoms/button/Button'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import { WidgetDesignModalContext } from './WidgetDesignModalProvider'

const PRODUCT_LOGIC_OPTIONS = [
  { value: 'BB', label: 'BB - この商品を買った人はこちらも購入' },
  { value: 'OB', label: 'OB - この商品を見た人はこちらを購入' },
  { value: 'personal', label: 'Personal - パーソナライズドレコメンド' },
  { value: 'similar', label: 'Similar - 類似商品' },
  { value: 'mail', label: 'Mail - メール配信用レコメンド' },
  { value: 'adaptive', label: 'Adaptive - 状況適応型レコメンド' },
  { value: 'ranking_blend', label: 'Ranking Blend - ランキング×パーソナル' },
  { value: 'recently_viewed', label: 'Recently Viewed - 最近見たものの続き' },
  { value: 'related_by_article', label: 'この特集に関連する商品 - 特集ページ向け' },
  {
    value: 'personal_by_article',
    label: 'この特集を見た人はこちらも見ています - 特集ページ向け'
  }
]
const ARTICLE_LOGIC_OPTIONS = [
  { value: 'article_trending', label: '人気記事 - 閲覧数が多い記事' },
  { value: 'article_similar', label: '関連記事 - AIタグが共通する記事' },
  { value: 'article_personal', label: 'この記事を読んだ人はこちらも' },
  { value: 'article_related_by_item', label: 'この商品に関連する記事 - 商品詳細ページ向け' }
]
const LOGIC_OPTIONS = [...PRODUCT_LOGIC_OPTIONS, ...ARTICLE_LOGIC_OPTIONS]
interface WidgetDesignModalInnerProps {
  widget: Widget
}

const WidgetDesignModalInner = ({ widget }: WidgetDesignModalInnerProps) => {
  const current = {
    layout_type: widget.layout_type || 'grid',
    title: widget.title_template || '',
    custom_css: widget.custom_css || '',
    max_items: widget.max_items || 4
  }

  const [sliderIdx, setSliderIdx] = React.useState(0)

  const layout = current.layout_type
  const title = current.title
  const customCss = current.custom_css
  const layoutClass = 'w2sr-layout-' + layout

  const logicLabel =
    LOGIC_OPTIONS.find((o) => o.value === widget.display_logic)?.label || widget.display_logic

  const sampleItems = [
    { name: 'サンプル商品 A', price: 2980, image: null },
    { name: 'サンプル商品 B', price: 1580, image: null },
    { name: 'サンプル商品 C', price: 3200, image: null },
    { name: 'サンプル商品 D', price: 4500, image: null },
    { name: 'サンプル商品 E', price: 1980, image: null },
    { name: 'サンプル商品 F', price: 3680, image: null }
  ]
  const visibleItems = sampleItems.slice(0, current.max_items)
  const slidesPerView = 4
  const maxSlide = Math.max(0, visibleItems.length - slidesPerView)

  const W2SR_CSS = `
    .w2sr-widget { font-family: -apple-system, BlinkMacSystemFont, sans-serif; margin: 0; }
    .w2sr-widget-title { font-size: 18px; font-weight: 700; color: #1e293b; margin: 0 0 16px; padding-bottom: 8px; border-bottom: 2px solid #2563eb; display: inline-block; }
    .w2sr-layout-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px; }
    .w2sr-layout-carousel { display: flex; gap: 12px; overflow-x: auto; scroll-snap-type: x mandatory; padding-bottom: 8px; }
    .w2sr-layout-carousel .w2sr-item { min-width: 140px; scroll-snap-align: start; }
    .w2sr-layout-slider { position: relative; overflow: hidden; }
    .w2sr-layout-slider .w2sr-slider-track { display: flex; transition: transform 0.4s ease; }
    .w2sr-layout-slider .w2sr-item { min-width: 25%; flex-shrink: 0; padding: 0 6px; box-sizing: border-box; }
    .w2sr-layout-slider .w2sr-slider-btn { position: absolute; top: 50%; transform: translateY(-50%); width: 32px; height: 32px; border-radius: 50%; border: 1px solid #cbd5e1; background: rgba(255,255,255,0.9); cursor: pointer; font-size: 16px; display: flex; align-items: center; justify-content: center; z-index: 2; color: #475569; }
    .w2sr-layout-slider .w2sr-slider-btn:hover { background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.12); }
    .w2sr-layout-slider .w2sr-slider-prev { left: 4px; }
    .w2sr-layout-slider .w2sr-slider-next { right: 4px; }
    .w2sr-layout-list { display: flex; flex-direction: column; gap: 8px; }
    .w2sr-layout-list .w2sr-item { flex-direction: row; }
    .w2sr-layout-list .w2sr-item-img { width: 80px; height: 80px; }
    .w2sr-item { display: flex; flex-direction: column; text-decoration: none; color: inherit; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
    .w2sr-item-img { width: 100%; aspect-ratio: 1; object-fit: cover; background: #e2e8f0; }
    .w2sr-item-info { padding: 8px; }
    .w2sr-item-name { font-size: 13px; font-weight: 500; color: #1e293b; margin: 0 0 4px; line-height: 1.3; }
    .w2sr-item-price { font-size: 14px; font-weight: 700; color: #dc2626; margin: 0; }
  `

  return (
    <ModalContent type={ModalContentType.FLEX_GROW}>
      <AreaContent>
        <div
          style={{
            fontSize: 12,
            color: '#64748b',
            margin: '0 0 8px',
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap'
          }}
        >
          <span>
            ロジック: <strong style={{ color: '#1e293b' }}>{logicLabel}</strong>
          </span>
          <span>
            レイアウト: <strong style={{ color: '#1e293b' }}>{layout}</strong>
          </span>
          <span>
            表示数: <strong style={{ color: '#1e293b' }}>{current.max_items}</strong>
          </span>
        </div>
        <div
          style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: 20,
            minHeight: 200
          }}
        >
          <style>{W2SR_CSS}</style>
          {customCss && <style>{customCss}</style>}
          <div className='w2sr-widget'>
            {title && <h3 className='w2sr-widget-title'>{title}</h3>}
            {layout === 'slider' ? (
              <div className={`w2sr-items ${layoutClass}`}>
                <button
                  className='w2sr-slider-btn w2sr-slider-prev'
                  onClick={() => setSliderIdx(Math.max(0, sliderIdx - 1))}
                  disabled={sliderIdx === 0}
                >
                  &lsaquo;
                </button>
                <div
                  className='w2sr-slider-track'
                  style={{ transform: `translateX(-${sliderIdx * 25}%)` }}
                >
                  {visibleItems.map((item, i) => (
                    <div key={i} className='w2sr-item'>
                      <div
                        className='w2sr-item-img'
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#94a3b8',
                          fontSize: 11
                        }}
                      >
                        No Image
                      </div>
                      <div className='w2sr-item-info'>
                        <p className='w2sr-item-name'>{item.name}</p>
                        <p className='w2sr-item-price'>&yen;{item.price.toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  className='w2sr-slider-btn w2sr-slider-next'
                  onClick={() => setSliderIdx(Math.min(maxSlide, sliderIdx + 1))}
                  disabled={sliderIdx >= maxSlide}
                >
                  &rsaquo;
                </button>
              </div>
            ) : (
              <div className={`w2sr-items ${layoutClass}`}>
                {visibleItems.map((item, i) => (
                  <div key={i} className='w2sr-item'>
                    <div
                      className='w2sr-item-img'
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#94a3b8',
                        fontSize: 11
                      }}
                    >
                      No Image
                    </div>
                    <div className='w2sr-item-info'>
                      <p className='w2sr-item-name'>{item.name}</p>
                      <p className='w2sr-item-price'>&yen;{item.price.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </AreaContent>
    </ModalContent>
  )
}

const WidgetDesignModal = () => {
  const { modal, showing, hide } = useContext(WidgetDesignModalContext)

  return (
    <Modal show={showing} type={ModalType.LARGE}>
      <ModalHeader text={'表示デザインプレビュー'} onClickClose={hide} />
      {modal.showing && <WidgetDesignModalInner widget={modal.widget} />}
      <ModalFooter>
        <Button color={'secondary'} size={'large'} onClick={hide}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default WidgetDesignModal
